/**
 * Notification Sound Service
 * Plays a subtle notification beep using Web Audio API
 */

class NotificationSoundService {
  constructor() {
    this.audioContext = null;
    this.isEnabled = this.loadPreference();
    this.lastPlayedTime = 0;
    this.cooldownMs = 5000; // Minimum 5 seconds between sounds
  }

  loadPreference() {
    const pref = localStorage.getItem('notification_sound_enabled');
    return pref === null ? true : pref === 'true';
  }

  savePreference(enabled) {
    localStorage.setItem('notification_sound_enabled', enabled.toString());
    this.isEnabled = enabled;
  }

  toggle() {
    this.savePreference(!this.isEnabled);
    return this.isEnabled;
  }

  initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext;
  }

  /**
   * Play a subtle notification beep
   * @param {string} type - 'success' | 'info' | 'warning' | 'error'
   */
  play(type = 'info') {
    if (!this.isEnabled) return;
    
    // Cooldown check
    const now = Date.now();
    if (now - this.lastPlayedTime < this.cooldownMs) return;
    this.lastPlayedTime = now;

    try {
      const ctx = this.initAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Different sounds for different notification types
      const sounds = {
        success: { freq: 880, duration: 0.15 },
        info: { freq: 660, duration: 0.12 },
        warning: { freq: 440, duration: 0.2 },
        error: { freq: 330, duration: 0.25 }
      };

      const sound = sounds[type] || sounds.info;
      
      oscillator.frequency.setValueAtTime(sound.freq, ctx.currentTime);
      oscillator.type = 'sine';
      
      // Gentle fade in/out for a pleasant sound
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + sound.duration);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + sound.duration);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  /**
   * Play a double-beep for important notifications
   */
  playImportant() {
    if (!this.isEnabled) return;
    
    const now = Date.now();
    if (now - this.lastPlayedTime < this.cooldownMs) return;
    this.lastPlayedTime = now;

    try {
      const ctx = this.initAudioContext();
      
      // First beep
      this.playBeep(ctx, 880, 0, 0.1);
      // Second beep (slightly higher, 150ms later)
      this.playBeep(ctx, 1100, 0.15, 0.1);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }

  playBeep(ctx, frequency, startTime, duration) {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + startTime);
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0, ctx.currentTime + startTime);
    gainNode.gain.linearRampToValueAtTime(0.12, ctx.currentTime + startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startTime + duration);

    oscillator.start(ctx.currentTime + startTime);
    oscillator.stop(ctx.currentTime + startTime + duration);
  }
}

// Singleton instance
const notificationSound = new NotificationSoundService();

export default notificationSound;
